import React from 'react';
import Footer from '../components/Footer';
import useScrollReveal from '../components/useScrollReveal';
import './Devotion.css';

function Reveal({ children, delay = '0s', className = '' }) {
  const ref = useScrollReveal();
  return (
    <div ref={ref} className={`reveal-block ${className}`} style={{ '--reveal-delay': delay }}>
      {children}
    </div>
  );
}

const saints = [
  {
    icon: '✦',
    name: 'Sister Marie de Saint-Pierre',
    years: '1816 – 1848',
    body: `A Carmelite nun in Tours, France, Sister Marie received a series of revelations
           beginning in 1843 in which Our Lord asked for a devotion to His Holy Face as
           reparation for blasphemy and the profanation of the Lord's Day and Holy Days' of Obligations. 
           She recorded these communications faithfully until her death at age 32.`,
  },
  {
    icon: '✦',
    name: 'Blessed Leo Dupont',
    years: '1797 – 1876',
    body: `Known as the "Holy Man of Tours," Leo Dupont established a chapel of the Holy
           Face in his home and promoted the devotion widely after receiving Sister Marie's
           writings. Miraculous healings were reported before his image of the Holy Face,
           drawing pilgrims from across France.`,
  },
  {
    icon: '✦',
    name: 'Saint Thérèse of Lisieux',
    years: '1873 – 1897',
    body: `The beloved Doctor of the Church took "of the Holy Face" as part of her
           religious name — Sister Thérèse of the Child Jesus and the Holy Face. She
           wrote with great depth of her love for the suffering, disfigured face of Christ,
           calling it the mirror of her own soul.`,
  },
  {
    icon: '✦',
    name: 'Pope Leo XIII',
    years: 'Pontificate 1878–1903',
    body: `Pope Leo XIII approved the Archconfraternity of the Holy Face in 1885 and
           granted indulgences to members. His approval gave the devotion official Church
           recognition and opened it to Catholics worldwide, establishing the Feast of the
           Holy Face on Shrove Tuesday.`,
  },
];

export default function Devotion() {
  return (
    <div className="page">
      {/* Hero */}
      <section className="devotion-hero">
        <div className="devotion-hero__bg" />
        <div className="devotion-hero__content fade-up">
          <span className="section-label">Living the Faith</span>
          <h1>Devotion Through<br />the Ages</h1>
          <p>Saints, witnesses, and the Church's embrace</p>
        </div>
      </section>

      {/* Intro quote */}
      <div className="devotion-quote-band">
        <Reveal>
          <blockquote className="devotion-quote">
            <p>
              "The devotion to the Holy Face is the devotion of reparation par excellence.
               Through it, souls atone for the sins that most wound the Heart of God."
            </p>
            <cite>— Pope Pius XI</cite>
          </blockquote>
        </Reveal>
      </div>

      {/* Saints cards */}
      <div className="section-inner">
        <Reveal>
          <span className="section-label">Key Figures</span>
          <h2 className="devotion-h2">Witnesses to the Holy Face</h2>
        </Reveal>

        <div className="saint-grid">
          {saints.map(({ icon, name, years, body }, i) => (
            <Reveal key={name} delay={`${i * 0.1}s`} className="saint-card">
              <div className="saint-card__icon">{icon}</div>
              <div className="saint-card__meta">
                <h3>{name}</h3>
                <span>{years}</span>
              </div>
              <p>{body}</p>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Timeline band */}
      <div className="devotion-dark">
        <div className="section-inner">
          <Reveal>
            <span className="section-label light">A Living Tradition</span>
            <h2>Milestones of the Devotion</h2>
          </Reveal>
          <div className="dev-timeline">
            {[
              { year: '1844', text: 'First revelations to Sister Marie de Saint-Pierre in Tours, France.' },
              { year: '1851', text: 'Blessed Leo Dupont establishes his chapel and begins promoting the devotion publicly.' },
              { year: '1873', text: 'Birth of Saint Thérèse of Lisieux, who will become the devotion\'s most celebrated apostle.' },
              { year: '1885', text: 'Pope Leo XIII formally approves the Archconfraternity of the Holy Face.' },
              { year: '1895', text: 'Saint Thérèse composes her "Act of Oblation to Merciful Love," deeply shaped by the Holy Face.' },
              { year: 'Today', text: 'The Feast of the Holy Face is celebrated on Shrove Tuesday each year by the universal Church.' },
            ].map(({ year, text }, i) => (
              <Reveal key={year} delay={`${i * 0.07}s`}>
                <div className="dev-timeline__item">
                  <span className="dev-timeline__year">{year}</span>
                  <p>{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
